import React, { useState } from 'react';
import FavouriteToggle from 'components/listing-card/favourite-toggle';
import Tooltip from 'components/control/tooltip';
import ShareIcon from 'components/icon/share-icon';
import formatCurrency from 'utils/format-currency';
import styles from './style.module.scss';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_ADDRESS_PAGE_SHARE_ICON } from 'constants/events';
import { useThemeContext } from 'contexts';
import { buildClassName } from 'utils/build-class-name';
import { ThemeNames } from 'types/themes';

import type Listing from 'data/listing';

interface Props {
  listing: Listing;
  isModal?: boolean;
  showFavouriteToggle?: boolean;
}

export default function ShareSaveListing({ listing, isModal, showFavouriteToggle = true }: Props) {
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const { theme, themeName } = useThemeContext();
  const host = theme.schemaUrl;
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;

  const share = () => {
    if (!isModal) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_SHARE_ICON);
    }
    let url = host + listing.addressPathWithFallback;
    url += url.match(/[?&]share/) ? '' : url.match(/\?/) ? '&share' : '?share';
    if (typeof navigator.share === 'undefined') {
      copyUrlToClipboard(url);
    } else {
      openNativeShareDialog(url);
    }
  };

  const copyUrlToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setIsTooltipActive(true);
      setTimeout(() => {
        setIsTooltipActive(false);
      }, 3000);
    }).catch(error => {
      console?.error('Clipboard api not available: ' + error);
    });
  };

  const openNativeShareDialog = (url: string) => {
    navigator.share({ title: document.title, url, text: buildShareText() }).catch(console.log);
  };

  const buildShareText = () => {
    const { seoStreet, soldPrice, price, isRental, bedrooms, bathrooms } = listing;
    return [
      'Check out this property on Zoocasa:',
      seoStreet,
      formatCurrency(soldPrice || price!) + (isRental ? '/month' : ''),
      `${bedrooms} beds, ${bathrooms} baths`,
    ].join('\n');
  };

  return (
    <div className={buildClassName(styles.component, isExpTheme && styles.exp)}>
      <div className={styles.share} onClick={share}>
        <label><ShareIcon /></label>
        <Tooltip className={styles['custom-tooltip-position']} content="Link Copied" isActive={isTooltipActive} />
      </div>
      {showFavouriteToggle && <FavouriteToggle listingId={listing.id} isInCarousel={true} isModal={isModal} isHeartExperiment={themeName === ThemeNames.ZOOCASA} />}
    </div>
  );
}
